import { type FC, type ReactNode, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, Calendar, History, User, Bell, CreditCard, HelpCircle, LogOut, Monitor, Settings } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadNotificationCount } from '../../hooks/useNotifications';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import type { DashboardSection } from '../../types/dashboard.types';

export interface DashboardSidebarProps {
  activeSection: DashboardSection;
  onSectionChange: (section: DashboardSection) => void;
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  id: DashboardSection;
  label: string;
  icon: ReactNode;
}

interface NotificationBadgeProps {
  count: number;
}

const NotificationBadge: FC<NotificationBadgeProps> = ({ count }) => {
  if (count === 0) return null;
  return (
    <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-red-500 text-white rounded-full">
      {count > 99 ? '99+' : count}
    </span>
  );
};

const navItems: NavItem[] = [
  { id: 'overview', label: 'Overview', icon: <Home className="w-5 h-5" /> },
  { id: 'bookings', label: 'Upcoming Bookings', icon: <Calendar className="w-5 h-5" /> },
  { id: 'history', label: 'History', icon: <History className="w-5 h-5" /> },
  { id: 'profile', label: 'Profile', icon: <User className="w-5 h-5" /> },
  { id: 'notifications', label: 'Notifications', icon: <Bell className="w-5 h-5" /> },
  { id: 'payment-methods', label: 'Payment Methods', icon: <CreditCard className="w-5 h-5" /> },
  { id: 'support', label: 'Help & Support', icon: <HelpCircle className="w-5 h-5" /> },
];

export const DashboardSidebar: FC<DashboardSidebarProps> = ({
  activeSection,
  onSectionChange,
  isOpen,
  onClose,
}) => {
  const { logout } = useAuth();
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const sidebarRef = useRef<HTMLElement>(null);

  // Trap focus within sidebar when open on mobile
  useFocusTrap(sidebarRef, { enabled: isOpen });

  // Close sidebar on Escape key
  useEscapeKey(onClose, isOpen);

  const handleNavClick = (section: DashboardSection) => {
    onSectionChange(section);
    onClose();
  };

  const handleLogout = async () => {
    await logout();
  };

  const sidebarClasses = cn(
    'fixed top-0 left-0 h-full w-64 bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700',
    'transform transition-transform duration-300 ease-in-out z-40',
    'flex flex-col',
    {
      'translate-x-0': isOpen,
      '-translate-x-full lg:translate-x-0': !isOpen,
    }
  );

  return (
    <aside
      ref={sidebarRef}
      id="dashboard-sidebar"
      role="navigation"
      className={sidebarClasses}
      aria-label="Dashboard navigation"
    >
      {/* Logo */}
      <Link
        to="/"
        className="flex items-center space-x-2 p-6 border-b border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
      >
        <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
          <span className="text-white font-bold text-lg">M</span>
        </div>
        <span className="text-xl font-bold text-gray-900 dark:text-slate-100">assaTherapy</span>
      </Link>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1" role="list">
          {navItems.map((item) => {
            const isActive = activeSection === item.id;
            return (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-6 py-3 text-left transition-colors',
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-l-4 border-primary-600 dark:border-primary-500'
                      : 'text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 border-l-4 border-transparent'
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className={isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-slate-400'}>
                    {item.icon}
                  </span>
                  <span className="font-medium">{item.label}</span>
                  {item.id === 'notifications' && (
                    <NotificationBadge count={unreadCount} />
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        {/* External Links */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
          <p className="px-6 mb-2 text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
            Account
          </p>
          <ul className="space-y-1" role="list">
            <li>
              <Link
                to="/sessions"
                onClick={onClose}
                className="flex items-center gap-3 px-6 py-3 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 border-l-4 border-transparent transition-colors"
              >
                <Monitor className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                <span className="font-medium">Active Devices</span>
              </Link>
            </li>
            <li>
              <Link
                to="/settings"
                onClick={onClose}
                className="flex items-center gap-3 px-6 py-3 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 border-l-4 border-transparent transition-colors"
              >
                <Settings className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                <span className="font-medium">Settings</span>
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      {/* Logout Button */}
      <div className="p-4 border-t border-gray-200 dark:border-slate-700">
        <button
          onClick={handleLogout}
          className={cn(
            'w-full flex items-center gap-3 px-4 py-3 rounded-lg',
            'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors'
          )}
        >
          <LogOut className="w-5 h-5 text-gray-500 dark:text-slate-400" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </aside>
  );
};
